// src/pages/mairie/Recensement.jsx
import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { config } from '../../config';
import { useGeolocation } from '../../hooks/useGeolocation';

// ✅ Fonction de reverse geocoding avec OpenStreetMap Nominatim
const reverseGeocode = async (lat, lng) => {
    try {
        const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&zoom=16&addressdetails=1&accept-language=fr`
        );
        if (!response.ok) throw new Error('Erreur de géocodage');
        const data = await response.json();
        
        if (data && data.display_name) {
            return data.display_name;
        }
        return null;
    } catch (error) {
        console.error('Erreur reverse geocoding:', error);
        return null;
    }
};

export default function Recensement() {
    const [step, setStep] = useState(1);
    const [recenseur, setRecenseur] = useState({
        nom: '',
        prenom: '',
        fonction: '',
        chef_equipe: '',
        numero: '',
        securite_policiere: ''
    });
    const [contribuable, setContribuable] = useState({
        numero_fiche: '',
        nom: '',
        prenom: '',
        telephone: '',
        superficie: '',
        description_calcul: '',
        montant_total: '',
        quartier: '',
        photo: null
    });
    const [taxesDisponibles, setTaxesDisponibles] = useState([]);
    const [taxesSelectionnees, setTaxesSelectionnees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [idRecenseur, setIdRecenseur] = useState(null);
    const [position, setPosition] = useState({ latitude: null, longitude: null });
    const [lieuNom, setLieuNom] = useState('');
    const [geocoding, setGeocoding] = useState(false);

    const { getLocation } = useGeolocation();

    // Charger les taxes disponibles
    useEffect(() => {
        const fetchTaxes = async () => {
            try {
                const token = localStorage.getItem('token');
                const res = await fetch(`${config.apiBase}/mairie/taxes.php`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await res.json();
                if (data.success) {
                    setTaxesDisponibles(data.taxes);
                }
            } catch (error) {
                toast.error('Erreur chargement taxes');
            }
        };
        fetchTaxes();
    }, []);

    // ✅ Récupérer la position et le nom du lieu
    const obtenirPositionEtLieu = async () => {
        try {
            const pos = await getLocation();
            if (pos) {
                setPosition({ latitude: pos.latitude, longitude: pos.longitude });
                
                // ✅ Reverse geocoding pour obtenir le nom du lieu
                setGeocoding(true);
                const nomLieu = await reverseGeocode(pos.latitude, pos.longitude);
                setGeocoding(false);
                
                if (nomLieu) {
                    setLieuNom(nomLieu);
                    // ✅ Pré-remplir le champ quartier avec le nom du lieu
                    setContribuable(prev => ({
                        ...prev,
                        quartier: nomLieu
                    }));
                    toast.success('📍 Position et lieu détectés');
                } else {
                    setLieuNom('Position non identifiée');
                }
            }
        } catch (error) {
            console.error('Erreur position:', error);
            toast.error('Impossible de récupérer la position');
        }
    };

    // Soumettre le recenseur
    const handleRecenseurSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${config.apiBase}/mairie/recenseurs.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(recenseur)
            });
            const data = await res.json();
            if (data.success) {
                setIdRecenseur(data.id_recenseur);
                toast.success('Recenseur enregistré');
                setStep(2);
                // ✅ Récupérer la position GPS et le lieu
                await obtenirPositionEtLieu();
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            toast.error('Erreur lors de l\'enregistrement');
        } finally {
            setLoading(false);
        }
    };

    // Gérer la sélection/désélection d'une taxe
    const handleTaxeToggle = (id_taxe) => {
        setTaxesSelectionnees(prev => {
            const exists = prev.find(t => t.id_taxe === id_taxe);
            if (exists) {
                return prev.filter(t => t.id_taxe !== id_taxe);
            } else {
                const taxe = taxesDisponibles.find(t => t.id_taxe === id_taxe);
                return [...prev, { id_taxe, montant_calcule: 0 }];
            }
        });
    };

    // Gérer le changement de montant pour une taxe
    const handleMontantChange = (id_taxe, montant) => {
        setTaxesSelectionnees(prev =>
            prev.map(t => t.id_taxe === id_taxe ? { ...t, montant_calcule: parseFloat(montant) || 0 } : t)
        );
    };

    // Soumettre le contribuable
    const handleContribuableSubmit = async (e) => {
        e.preventDefault();
        if (!idRecenseur) {
            toast.error('Recenseur non enregistré');
            return;
        }
        if (taxesSelectionnees.length === 0) {
            toast.error('Sélectionnez au moins une taxe');
            return;
        }
        if (!contribuable.montant_total || parseFloat(contribuable.montant_total) <= 0) {
            toast.error('Le montant total est requis et doit être supérieur à 0');
            return;
        }
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            
            // Construction du payload en JSON
            const payload = {
                numero_fiche: contribuable.numero_fiche || '',
                nom: contribuable.nom,
                prenom: contribuable.prenom,
                telephone: contribuable.telephone,
                superficie: contribuable.superficie || 0,
                description_calcul: contribuable.description_calcul || '',
                montant_total: parseFloat(contribuable.montant_total) || 0,
                quartier: contribuable.quartier || lieuNom || '',
                latitude: position.latitude || null,
                longitude: position.longitude || null,
                id_recenseur: idRecenseur,
                taxes: taxesSelectionnees
            };

            // Gestion de la photo
            if (contribuable.photo) {
                const reader = new FileReader();
                const photoBase64 = await new Promise((resolve) => {
                    reader.onloadend = () => resolve(reader.result);
                    reader.readAsDataURL(contribuable.photo);
                });
                payload.photo = photoBase64;
            }

            const res = await fetch(`${config.apiBase}/mairie/contribuables.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });
            
            const data = await res.json();
            if (data.success) {
                toast.success(`Contribuable enregistré avec succès ! N° Fiche: ${data.numero_fiche}`);
                // Réinitialiser le formulaire
                setContribuable({
                    numero_fiche: '',
                    nom: '',
                    prenom: '',
                    telephone: '',
                    superficie: '',
                    description_calcul: '',
                    montant_total: '',
                    quartier: '',
                    photo: null
                });
                setTaxesSelectionnees([]);
                setLieuNom('');
                // Réinitialiser le champ photo
                document.getElementById('photoInput').value = '';
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            console.error('Erreur:', error);
            toast.error('Erreur lors de l\'enregistrement');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto p-6">
            <h1 className="text-2xl font-bold mb-6">Recensement des contribuables</h1>

            {/* Étape 1 : Enregistrement du recenseur */}
            {step === 1 && (
                <form onSubmit={handleRecenseurSubmit} className="bg-white p-6 rounded shadow space-y-4">
                    <h2 className="text-lg font-semibold">Informations du recenseur</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input 
                            className="border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none" 
                            placeholder="Nom" 
                            value={recenseur.nom} 
                            onChange={e => setRecenseur({...recenseur, nom: e.target.value})} 
                            required 
                        />
                        <input 
                            className="border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none" 
                            placeholder="Prénom" 
                            value={recenseur.prenom} 
                            onChange={e => setRecenseur({...recenseur, prenom: e.target.value})} 
                            required 
                        />
                        <input 
                            className="border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none" 
                            placeholder="Fonction" 
                            value={recenseur.fonction} 
                            onChange={e => setRecenseur({...recenseur, fonction: e.target.value})} 
                        />
                        <input 
                            className="border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none" 
                            placeholder="Chef d'équipe" 
                            value={recenseur.chef_equipe} 
                            onChange={e => setRecenseur({...recenseur, chef_equipe: e.target.value})} 
                        />
                        <input 
                            className="border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none" 
                            placeholder="Numéro de téléphone" 
                            value={recenseur.numero} 
                            onChange={e => setRecenseur({...recenseur, numero: e.target.value})} 
                            required 
                        />
                        <input 
                            className="border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none" 
                            placeholder="Sécurité policière" 
                            value={recenseur.securite_policiere} 
                            onChange={e => setRecenseur({...recenseur, securite_policiere: e.target.value})} 
                        />
                    </div>
                    <button 
                        type="submit" 
                        disabled={loading} 
                        className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                    >
                        {loading ? 'Enregistrement...' : 'Enregistrer le recenseur'}
                    </button>
                </form>
            )}

            {/* Étape 2 : Enregistrement du contribuable */}
            {step === 2 && (
                <form onSubmit={handleContribuableSubmit} className="bg-white p-6 rounded shadow space-y-4">
                    <h2 className="text-lg font-semibold">Informations du contribuable</h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Numéro de fiche (optionnel) */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Numéro de fiche <span className="text-xs text-gray-400">(optionnel - laissé vide, généré automatiquement)</span>
                            </label>
                            <input 
                                className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none" 
                                placeholder="Ex: F-202401-00001" 
                                value={contribuable.numero_fiche} 
                                onChange={e => setContribuable({...contribuable, numero_fiche: e.target.value})} 
                            />
                        </div>
                        
                        <input 
                            className="border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none" 
                            placeholder="Nom *" 
                            value={contribuable.nom} 
                            onChange={e => setContribuable({...contribuable, nom: e.target.value})} 
                            required 
                        />
                        <input 
                            className="border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none" 
                            placeholder="Prénom *" 
                            value={contribuable.prenom} 
                            onChange={e => setContribuable({...contribuable, prenom: e.target.value})} 
                            required 
                        />
                        <input 
                            className="border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none" 
                            placeholder="Téléphone *" 
                            value={contribuable.telephone} 
                            onChange={e => setContribuable({...contribuable, telephone: e.target.value})} 
                            required 
                        />
                        <input 
                            className="border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none" 
                            placeholder="Superficie (m²)" 
                            type="number" 
                            value={contribuable.superficie} 
                            onChange={e => setContribuable({...contribuable, superficie: e.target.value})} 
                        />
                        
                        {/* Montant total */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Montant total (FCFA) * <span className="text-xs text-gray-400">(Calcul hors système)</span>
                            </label>
                            <input 
                                className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none" 
                                placeholder="Ex: 150000" 
                                type="number" 
                                value={contribuable.montant_total} 
                                onChange={e => setContribuable({...contribuable, montant_total: e.target.value})} 
                                required 
                            />
                        </div>
                        
                        <textarea 
                            className="border p-2 rounded col-span-2 focus:ring-2 focus:ring-blue-500 outline-none" 
                            placeholder="Description du calcul" 
                            rows="2"
                            value={contribuable.description_calcul} 
                            onChange={e => setContribuable({...contribuable, description_calcul: e.target.value})} 
                        />
                        
                        {/* ✅ Quartier avec suggestion automatique */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Quartier / Lieu
                                {geocoding && <span className="text-xs text-gray-400 ml-2">(Recherche en cours...)</span>}
                            </label>
                            <div className="flex gap-2">
                                <input 
                                    className="flex-1 border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none" 
                                    placeholder="Quartier ou lieu" 
                                    value={contribuable.quartier} 
                                    onChange={e => setContribuable({...contribuable, quartier: e.target.value})} 
                                />
                                {position.latitude && (
                                    <button
                                        type="button"
                                        onClick={obtenirPositionEtLieu}
                                        disabled={geocoding}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 whitespace-nowrap text-sm"
                                    >
                                        {geocoding ? '⏳...' : '📍 Détecter'}
                                    </button>
                                )}
                            </div>
                            {lieuNom && (
                                <p className="text-xs text-green-600 mt-1">
                                    ✅ Lieu détecté: {lieuNom}
                                </p>
                            )}
                            {position.latitude && !lieuNom && !geocoding && (
                                <p className="text-xs text-gray-400 mt-1">
                                    📍 Position: {position.latitude.toFixed(6)}, {position.longitude.toFixed(6)}
                                </p>
                            )}
                        </div>
                        
                        {/* Photo */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Photo (optionnelle)</label>
                            <input 
                                id="photoInput"
                                type="file" 
                                accept="image/*" 
                                onChange={e => setContribuable({...contribuable, photo: e.target.files[0]})} 
                                className="w-full border p-1 rounded"
                            />
                        </div>
                    </div>

                    {/* Section Taxes avec cases à cocher */}
                    <div className="border-t pt-4 mt-4">
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                            Taxes applicables (cochez celles qui s'appliquent)
                        </label>
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                            {taxesDisponibles.map(taxe => {
                                const selected = taxesSelectionnees.find(t => t.id_taxe === taxe.id_taxe);
                                return (
                                    <div key={taxe.id_taxe} className="flex flex-wrap items-center gap-4 p-3 border rounded-lg hover:bg-gray-50">
                                        <div className="flex items-center gap-2 min-w-[180px]">
                                            <input
                                                type="checkbox"
                                                checked={!!selected}
                                                onChange={() => handleTaxeToggle(taxe.id_taxe)}
                                                className="w-4 h-4 text-blue-600 rounded"
                                            />
                                            <label className="font-medium">{taxe.nom}</label>
                                        </div>
                                        <div className="flex items-center gap-2 flex-wrap flex-1">
                                            <span className="text-sm text-gray-500">Montant:</span>
                                            <input
                                                type="number"
                                                value={selected?.montant_calcule || ''}
                                                onChange={(e) => handleMontantChange(taxe.id_taxe, e.target.value)}
                                                disabled={!selected}
                                                placeholder="0"
                                                className={`w-32 px-2 py-1 border rounded text-sm ${!selected ? 'bg-gray-100' : ''}`}
                                            />
                                            <span className="text-xs text-gray-400">FCFA</span>
                                            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
                                                {taxe.periode === 'annuelle' ? 'Annuelle' :
                                                 taxe.periode === 'mensuelle' ? 'Mensuelle' : 'Journalière'}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {taxesSelectionnees.length > 0 && (
                            <div className="mt-2 text-sm text-green-600">
                                ✅ {taxesSelectionnees.length} taxe(s) sélectionnée(s)
                            </div>
                        )}
                    </div>

                    <button 
                        type="submit" 
                        disabled={loading} 
                        className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                    >
                        {loading ? 'Enregistrement...' : 'Enregistrer le contribuable'}
                    </button>
                </form>
            )}
        </div>
    );
}